import { AllPage } from "./AllPage";
import { CapturePage } from "../capture/CapturePage";
import { InstallPage } from "../capture/InstallPage";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AppShell } from "./AppShell";
import { LoginPage } from "./LoginPage";
import { useSession } from "./session";
import { HomePage } from "./HomePage";
import { SettingsPage } from "./SettingsPage";
import { setActor } from "../core/actor";
import { loadPlugins } from "../registry/pluginLoader";
import { WorklogPage } from "../tracking/WorklogPage";
import { WorklogExportPage } from "../tracking/WorklogExportPage";
import { WelcomePage } from "./WelcomePage";
import { RootRedirect } from "./RootRedirect";
import { TaskRedirect } from "./TaskRedirect";
import { DashboardPage } from "../dashboards/DashboardPage";
import { FocusPage } from "../focus/FocusPage";
import { BigClockPage } from "../focus/BigClockPage";
import { PlannerPage } from "../planner/PlannerPage";
import { WhoPage } from "../circle/WhoPage";
import { JoinPage } from "../circle/JoinPage";

export function App() {
  const { t } = useTranslation();
  const { session, loading, error, load } = useSession();

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    setActor(session?.profile?.id);
    // enabled plugins register their view/task types after boot (spec §9.10)
    if (session?.principal) void loadPlugins();
  }, [session?.profile?.id, session?.principal]);

  if (loading && !session) {
    return (
      <div className="grid min-h-dvh place-items-center text-neutral-500" role="status">
        {t("session.loading")}
      </div>
    );
  }
  if (error || !session) {
    return (
      <div className="mx-auto max-w-md p-8 text-neutral-500" role="alert">
        {t(error ?? "error.generic")}
      </div>
    );
  }
  if (!session.principal) {
    // circle: the picker is the entry screen; an invite link opens the join form (spec §9.29)
    const join = location.pathname.match(/^\/join\/([^/]+)/);
    if (session.mode === "circle") return join ? <JoinPage token={join[1]} /> : <WhoPage />;
    return <LoginPage />;
  }
  return (
    <BrowserRouter>
      <Routes>
        <Route path="welcome" element={<WelcomePage />} />
        <Route path="focus/:taskId" element={<FocusPage />} />
        <Route path="focus" element={<BigClockPage />} />
        {/* the big view was a page of its own: old bookmarks land on Focus, large */}
        <Route path="clock" element={<Navigate to="/focus" replace />} />
        <Route path="c" element={<CapturePage />} />
        <Route path="share" element={<CapturePage />} />
        <Route path="install" element={<InstallPage />} />
        <Route element={<AppShell />}>
          <Route index element={<RootRedirect />} />
          <Route path="list" element={<HomePage />} />
          <Route path="d/:slug" element={<DashboardPage />} />
          <Route path="t/:taskId" element={<TaskRedirect />} />
          <Route path="planner" element={<PlannerPage />} />
          <Route path="planner/:date" element={<PlannerPage />} />
          <Route path="summary" element={<Navigate to="/worklog" replace />} />
          <Route path="all" element={<AllPage />} />
          <Route path="worklog" element={<WorklogPage />} />
          <Route path="worklog/export" element={<WorklogExportPage />} />
          <Route path="worklog/:date" element={<WorklogPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
